import moment from 'moment';
import {
  getChildPos,
  prepareBulkReorderAtEnd,
} from './ordered-relation.js';
import { defaultListID } from './reactive-cache.js';
import {
  Item,
  generateId,
} from './item.js';
import { isSubtask } from './utils.js';
import { calcRepeatDate } from './time-utils.js'

function repeatingDoesntMakeSense(task) {
  if (!task.plannedFor) { return true; }
  else {
    if (task.repeat.increment == undefined || task.repeat.unit == undefined)
      return true;
    const timeValues = {
      day: 1,
      week: 7,
      month: 30,
      year: 365,
      backlog: Infinity,
    }
    const repeatTime = task.repeat.increment * timeValues[task.repeat.unit]
    const plannedForScope = timeValues[task.plannedFor.parentClass]
    return repeatTime < plannedForScope;
  }
}

function handleRepeatingTask(task, vue) {
  if (task.repeat.repeated) {
    alert(vue.$gettext('Next occurence already scheduled.'))
  } else {
    if (repeatingDoesntMakeSense(task)) {
      alert(vue.$gettext('This repetition setting does not make sense.'))
      return
    }
    const destination = {
      parentClass: task.plannedFor.parentClass,
      parentKey: calcRepeatDate(task),
      seq: task.plannedFor.seq,
    }
    duplicateItem(vue.$reactiveCache, task, destination, true);
    task.repeat.repeated = true;
    const msg = vue.$gettext('Next occurrence scheduled in') +
      ` ${task.repeat.increment} ${task.repeat.unit}`;
    vue.$root.$emit('snackbar', { msg })
  }
}

export async function toggleDoneness(item, vue) {
  const doable = ['task', 'goal'].includes(item.type);
  if (!doable) return;

  const copy = item.clone();
  const doneMomentOrNull = !item.doneAtTimestampMs ? moment.utc() : null;

  if (doneMomentOrNull) {
    copy.doneAtTimestampMs = doneMomentOrNull.valueOf();
    if (copy.repeat) {
      handleRepeatingTask(copy, vue);
    }
  } else {
    delete copy.doneAtTimestampMs;
  }

  await copy.save();

  if (doneMomentOrNull) {
    const msg = vue.$gettext('Marked as done.');
    const buttonText = !!vue.$root.showDone
      ? vue.$gettext('hide done tasks')
      : vue.$gettext('show done tasks');
    const callback = () => { vue.$root.showDone = !vue.$root.showDone };
    vue.$root.$emit('snackbar', { msg, buttonText, callback });
  }
}

export function archiveItems(items, vue) {
  const ts = moment.utc().valueOf();
  Promise.all(items.map(
    (item) => item.clone({ archivedTimestampMs: ts }).save()
  ))

  const msg = items.length === 1
    ? vue.$gettext('Item archived.')
    : items.length + vue.$gettext(' items archived.')
  const buttonText = vue.$gettext('undo')
  const itemIds = items.map(item => item._id);

  vue.$root.$emit("snackbar", {
    msg,
    buttonText,
    callback: () => {
      // We need to re-fetch from cache to have updated rev.
      unarchiveItems(itemIds.map((id) => vue.$reactiveCache.docs[id]))
    },
    closeOnClick: true,
  });
}

export function unarchiveItems(items) {
  return Promise.all(items.map(
    (item) => item.clone({ delKey: 'archivedTimestampMs' }).save()
  ))
}

export function deleteItem(taskToDelete, reactiveCache) {
  for (const elem of taskToDelete.getChildren()) {
    deleteItem(elem, reactiveCache)
  };
  return taskToDelete.clone({ _deleted: true }).save();
}

export function bulkDelete(tasksToDelete) {
  return Promise.all(tasksToDelete.map(
    (task) => task.clone({ _deleted: true }).save()
  ))
}

export function setTaskColor(taskToEdit, color) {
  return taskToEdit.clone({ color: color }).save();
}

export function clearColor(taskToEdit) {
  return taskToEdit.clone({ delKey: 'color' }).save()
}

export function addTaskRepeat(taskToEdit) {
  return !taskToEdit.repeat
    ? taskToEdit.clone({ repeat: {} }).save()
    : true
}

export function configureRepeat(taskToEdit, attrName, attrValue) {
  const updatedTask = taskToEdit.clone()
  updatedTask.repeat[attrName] = attrValue;
  updatedTask.repeat.repeated = false;
  updatedTask.save()
}

export function removeRepeat(taskToEdit) {
  return taskToEdit.clone({ delKey: 'repeat' }).save()
}

export function addLabels(taskToEdit) {
  return !taskToEdit.labels
    ? new taskToEdit.clone({ labels: [] }).save()
    : true
}
export function addLabel(taskToEdit, label) {
  const labels = taskToEdit.labels || [];
  labels.push(label);
  return taskToEdit.clone({ labels }).save()
}

export function updateLabels(taskToEdit, labels) {
  return taskToEdit.clone({ labels }).save()
}

export function foldTask(task) {
  return task.updateLocalData({ folded: true });
}

export function unfoldTask(task) {
  return task.updateLocalData({ folded: false });
}

export function changeType(item, newType) {
  return item.clone({ type: newType }).save();
}

export function convertItemToList(item) {
  const newId = 'list:' + item._id.split(':')[1];
  const doc = Object.assign({}, item)
  delete doc._rev;
  delete doc.subtaskOf;
  delete doc.plannedFor;
  doc.type = 'list';
  doc._id = newId;
  new Item(doc, item.cache, item.couch).save();
  for (const el of item.getChildren()) {
    const newPPtr = {
      parentKey: newId,
      parentClass: 'list',
      seq: el.subtaskOf.seq,
    }
    el.clone({ subtaskOf: newPPtr }).save();
  }
  item.clone({ _deleted: true }).save();
  return newId;
}

export function convertListToItem(list, newType) {
  const newId = 'task:' + list._id.split(':')[1];
  const doc = Object.assign({}, list)
  delete doc._rev;
  doc.type = newType;
  doc._id = newId;
  const newItem = new Item(doc, list.cache, list.couch)
  const tasksToSave = prepareBulkReorderAtEnd({
    reactiveCache: list.cache,
    objects: [newItem],
    newPPtr: {
      parentClass: 'list',
      parentKey: 'backlog',
    },
    relation: 'plannedFor'
  });
  Promise.all(tasksToSave.map(
    (task) => task.save()
  ))
  for (const el of list.getChildren()) {
    const update = {
      subtaskOf: {
        parentKey: newId,
        parentClass: 'task',
        seq: el.subtaskOf.seq,
      }
    }
    if (el?.plannedFor?.parentKey === "backlog") {
      update.delKey = 'plannedFor'
    }
    el.clone(update).save();
  }
  list.clone({ _deleted: true }).save();
  return newId;
}


// Finds the new parent (newPPtr) and insertPos for "detaching" the task(s).
// Note: subtaskOf field should be deleted before passing the task(s) to
// reorderTasks.
// Returns null if no sufficient parent is found.
export function findMoveToParentTarget(reactiveCache, parentTask) {
  // until "planning" feature is implemented, we can assume that
  // subtaskOf and plannedFor are mutually exclusive.
  if (isSubtask(parentTask)) {
    return {
      relation: 'subtaskOf',
      newPPtr: parentTask.subtaskOf,
      insertPos: getChildPos(reactiveCache, parentTask, 'subtaskOf'),
    };
  } else if (parentTask.plannedFor) {
    return {
      relation: 'plannedFor',
      newPPtr: parentTask.plannedFor,
      insertPos: getChildPos(reactiveCache, parentTask, 'plannedFor'),
    };
  }
  // No new parent.
  return null;
}

export function duplicateItem(reactiveCache, item, dest = null, duringRepeat = false) {
  const clearedAttributes = {
    _id: generateId("task"),
    _rev: undefined,
    doneAtTimestampMs: undefined
  }
  const newItem = item.clone(clearedAttributes);
  if (newItem.repeat) {
    // copy original values, but create a new object.
    // Otherwise closing the original item will affect new instance.
    newItem.repeat = {
      increment: newItem.repeat.increment,
      unit: newItem.repeat.unit,
    };
  }
  if (dest) {
    if (item.plannedFor) newItem.plannedFor = dest
    if (isSubtask(item) && !duringRepeat) newItem.subtaskOf = dest
  }
  newItem.save()

  for (const element of item.getChildren()) {
    const destination = {
      parentClass: 'task',
      parentKey: newItem._id,
      seq: element.subtaskOf.seq,
    }
    duplicateItem(reactiveCache, element, destination)
  }
}

export function moveToOtherDB(item, destDB, reactiveCache) {
  const copy = item.clone()
  if (item?.subtaskOf?.parentClass === 'list') {
    const targetList = defaultListID(destDB)
    // Don't modify original object assigned to 'subtaskOf' attribute.
    copy.subtaskOf = {
      parentClass: item.subtaskOf.parentClass,
      parentKey: targetList,
      seq: item.subtaskOf.seq,
    }
  }
  changeDBrecursively(copy, destDB, reactiveCache)
}

export function changeDBrecursively(item, destDB, reactiveCache) {
  const copy = item.clone({ _rev: undefined })

  for (const elem of item.getChildren()) {
    changeDBrecursively(elem, destDB, reactiveCache)
  }
  item.clone({ _deleted: true }).save();
  destDB.put(copy)
}
